import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { InvoiceStats } from '../components/invoices/InvoiceStats';
import { InvoiceList } from '../components/invoices/InvoiceList';
import { InvoiceForm } from '../components/invoices/InvoiceForm';
import { InvoiceDetail } from '../components/invoices/InvoiceDetail';
import { PaymentForm } from '../components/payments/PaymentForm';
import { invoiceService } from '../services/invoiceService';
import { InvoiceFilters, CreateInvoiceData, UpdateInvoiceData, Invoice } from '../types/invoice';

type PageMode = 'list' | 'create' | 'edit' | 'view' | 'payment';

export const InvoicesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<PageMode>('list');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // Open create mode with job context when navigating from job "Create Invoice"
  const createForJob = (location.state as { createForJob?: { jobId: string; customerId: string } })?.createForJob;
  useEffect(() => {
    if (createForJob?.jobId && createForJob?.customerId) {
      setMode('create');
    }
  }, [createForJob?.jobId, createForJob?.customerId]);

  // Fetch invoice if id is in URL
  const { data: invoiceData } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoiceService.getInvoice(id!),
    enabled: !!id && mode === 'list', // Only fetch if we have an id and not already in a mode
  });

  // When id changes or invoice data loads, set view mode (do not override create mode when createForJob)
  useEffect(() => {
    if (id && invoiceData) {
      setSelectedInvoice(invoiceData);
      setMode('view');
    } else if (!id && !createForJob) {
      setMode('list');
      setSelectedInvoice(null);
    }
  }, [id, invoiceData, createForJob]);
  const [filters, setFilters] = useState<InvoiceFilters>({
    page: 1,
    limit: 10
  });

  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: invoiceService.createInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      navigate('/invoices', { replace: true, state: {} });
      setMode('list');
    },
    onError: (error) => {
      console.error('Failed to create invoice:', error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvoiceData }) => 
      invoiceService.updateInvoice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', selectedInvoice?.id] });
      setMode('list');
      setSelectedInvoice(null);
    },
    onError: (error) => {
      console.error('Failed to update invoice:', error);
    }
  });

  const handleCreateInvoice = (data: CreateInvoiceData) => {
    createMutation.mutate(data);
  };

  const handleUpdateInvoice = (data: CreateInvoiceData) => {
    if (selectedInvoice) {
      updateMutation.mutate({ 
        id: selectedInvoice.id, 
        data: {
          ...data,
          items: data.items
        }
      });
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setMode('view');
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setMode('edit');
  };

  const handlePaymentInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setMode('payment');
  };

  const handleBackToList = () => {
    navigate('/invoices', { replace: true, state: {} });
    setMode('list');
    setSelectedInvoice(null);
  };

  const handleFilterChange = (newFilters: Partial<InvoiceFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  // Show create form
  if (mode === 'create') {
    return (
      <div style={{ padding: 24 }}>
        <InvoiceForm
          initialCustomerId={createForJob?.customerId}
          initialJobId={createForJob?.jobId}
          onSubmit={handleCreateInvoice}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Show edit form
  if (mode === 'edit' && selectedInvoice) {
    return (
      <div style={{ padding: 24 }}>
        <InvoiceForm
          invoice={selectedInvoice}
          onSubmit={handleUpdateInvoice}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Show invoice detail view
  if (mode === 'view' && selectedInvoice) {
    return (
      <InvoiceDetail
        invoiceId={selectedInvoice.id}
        onEdit={handleEditInvoice}
        onPayment={handlePaymentInvoice}
        onBack={handleBackToList}
      />
    );
  }

  // Show payment form
  if (mode === 'payment' && selectedInvoice) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={handleBackToList}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Invoices
          </button>
        </div>
        <PaymentForm
          invoice={selectedInvoice}
          onSuccess={handleBackToList}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    background: '#f4efe2',
    border: '1px solid rgba(92,78,51,0.25)',
    borderRadius: 4,
    fontFamily: "'Inter', sans-serif",
    fontSize: 12,
    color: '#5b5a52',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#6b6a5c',
    display: 'block',
    marginBottom: 6,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
              Sales
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
              Invoices
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
              Manage your invoices and billing
            </div>
          </div>
          <button
            onClick={() => setMode('create')}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #d97706, #b45309)',
              color: '#1c2033',
              border: 'none',
              padding: '12px 24px',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 32px rgba(217,119,6,0.25)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Invoice
          </button>
        </div>
      </div>

      <InvoiceStats />

      {/* Filters */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 16 }}>
          <div>
            <label style={labelStyle}>Search</label>
            <input
              type="text"
              placeholder="Search invoices..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              style={inputStyle}
              onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(217,119,6,0.35)'; }}
              onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(92,78,51,0.25)'; }}
            />
          </div>

          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange({ status: e.target.value as any })}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SENT">Sent</option>
              <option value="VIEWED">Viewed</option>
              <option value="OVERDUE">Overdue</option>
              <option value="PAID">Paid</option>
              <option value="PARTIALLY_PAID">Partially Paid</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Items per page</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => setFilters({ page: 1, limit: 10 })}
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#6b6a5c',
                background: 'rgba(239,232,213,0.5)',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 4,
                padding: '9px 16px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#d97706'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(217,119,6,0.25)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#8a8778'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(92,78,51,0.22)'; }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <InvoiceList
        filters={filters}
        onView={handleViewInvoice}
        onEdit={handleEditInvoice}
      />
    </div>
  );
};